/**
 * Sign-in view component
 * Displays email/password and OAuth sign-in options
 */

import {
  signInWithGoogle,
  signInWithFacebook,
  signInWithEmail,
  signUpWithEmail,
  sendPasswordReset,
  linkCredentialToCurrentUser,
} from '../services/auth';
import { t, i18next } from '../i18n/config';
import { showLoadingOverlay, hideLoadingOverlay } from '../utils/loadingIndicator.js';

/**
 * Create and render the sign-in view
 */
export function renderSignInView() {
  const app = document.getElementById('app');

  // Check if this is an invitation URL
  const urlParams = new URLSearchParams(window.location.search);
  const invitationToken = urlParams.get('token');
  const isInvitation = invitationToken && window.location.pathname === '/invite';

  // Get current language
  const currentLang = i18next.language || localStorage.getItem('i18nextLng') || 'af';

  const invitationMessage = isInvitation
    ? `
    <div class="invitation-notice">
      <div class="invitation-icon">🎉</div>
      <h2>${t('invitation.welcome', "You've been invited!")}</h2>
      <p>${t('invitation.signInRequired', 'Please sign in to accept this invitation')}</p>
    </div>
  `
    : '';

  app.innerHTML = `
    <div class="sign-in-container">
      <div class="sign-in-card">

      <!-- Language Selector -->
      <div class="language-selector">
        <button id="langEn" class="lang-btn ${currentLang === 'en' ? 'active' : ''}" data-lang="en">English</button>
        <button id="langAf" class="lang-btn ${currentLang === 'af' ? 'active' : ''}" data-lang="af">Afrikaans</button>
      </div>

      <div class="logo-container">
        <img src="https://kassie.media/svg/branding/KassieNuweBlou-2.svg" alt="Kassie" class="logo"/>
      </div>
      ${invitationMessage}
      <h1>${isInvitation ? t('auth.signIn') : t('auth.welcome')}</h1>

        <!-- Social Sign-In Buttons (top, prominent) -->
        <div class="sign-in-buttons" style="margin-bottom: 16px;">
          <button id="googleSignInBtn" class="sign-in-btn google-btn">
            <img src="/images/google.svg" alt="Google" />
          </button>
          <button id="showEmailFormBtn" class="sign-in-btn email-btn">
            <img src="/images/email.svg" alt="Email" />
          </button>
          <button id="facebookSignInBtn" class="sign-in-btn facebook-btn">
            <img src="/images/facebook.svg" alt="Facebook" />
          </button>
        </div>

        <!-- Email/Password Form (hidden by default, revealed on email button click) -->
        <div class="email-auth-section" id="emailAuthSection" style="display: none;">
          <form id="emailAuthForm" class="email-auth-form" novalidate>
            <div class="form-group" id="fullNameGroup" style="display: none;">
              <input
                type="text"
                id="fullName"
                name="fullName"
                autocomplete="name"
                placeholder="${t('auth.nameAndSurname', 'Name and Surname')}"
              />
              <span class="field-error" id="fullNameError"></span>
            </div>

            <div class="form-group">
              <input
                type="email"
                id="email"
                name="email"
                autocomplete="email"
                placeholder="${t('auth.email', 'Email')}"
              />
              <span class="field-error" id="emailError"></span>
            </div>

            <div class="form-group">
              <input
                type="password"
                id="password"
                name="password"
                autocomplete="current-password"
                placeholder="${t('auth.password', 'Password')}"
              />
              <span class="field-error" id="passwordError"></span>
            </div>

            <div class="form-actions">
              <button type="submit" id="signInBtn" class="btn-primary">
                ${t('auth.signInAction', 'Sign In')}
              </button>
            </div>

            <div class="auth-links">
              <button type="button" id="forgotPasswordBtn" class="btn-link">
                ${t('auth.forgotPassword', 'Forgot password?')}
              </button>
              <button type="button" id="showRegisterBtn" class="btn-link">
                ${t('auth.register', 'Register')}
              </button>
            </div>
          </form>

          <!-- Error and Success Messages -->
          <div id="errorMessage" class="error-message" style="display: none;"></div>
          <div id="successMessage" class="success-message" style="display: none;"></div>
        </div>

        <!-- Account Linking Modal -->
        <div id="linkingModal" class="modal" style="display: none;">
          <div class="modal-content">
            <h3>${t('auth.linkAccounts', 'Link Accounts')}</h3>
            <p id="linkingMessage"></p>
            <div class="modal-actions">
              <button id="confirmLinkBtn" class="btn-primary">${t('auth.linkNow', 'Link Now')}</button>
              <button id="cancelLinkBtn" class="btn-secondary">${t('app.cancel', 'Cancel')}</button>
            </div>
          </div>
        </div>

      <p class="sign-in-card__privacy-link">
        ${t('auth.privacyPolicyLink')} <a href="https://kassie.media/privacy" target="_blank" rel="noopener noreferrer">${t('auth.privacyPolicy')}</a>
      </p>
      </div>
    </div>
  `;

  // Add event listeners
  document.getElementById('langEn').addEventListener('click', () => changeLanguage('en'));
  document.getElementById('langAf').addEventListener('click', () => changeLanguage('af'));
  document.getElementById('emailAuthForm').addEventListener('submit', handleEmailSignIn);
  document.getElementById('forgotPasswordBtn').addEventListener('click', handleForgotPassword);
  document.getElementById('googleSignInBtn').addEventListener('click', handleGoogleSignIn);
  document.getElementById('facebookSignInBtn').addEventListener('click', handleFacebookSignIn);
  document.getElementById('cancelLinkBtn').addEventListener('click', hideLinkingModal);

  // "Register" link shows the registration form (name + email + password)
  document.getElementById('showRegisterBtn').addEventListener('click', () => {
    const fullNameGroup = document.getElementById('fullNameGroup');
    const signInBtn = document.getElementById('signInBtn');
    const showRegisterBtn = document.getElementById('showRegisterBtn');
    
    if (fullNameGroup.style.display === 'none') {
      // Switch to register mode
      fullNameGroup.style.display = 'block';
      signInBtn.textContent = t('auth.createAccount', 'Create Account');
      signInBtn.dataset.mode = 'register';
      showRegisterBtn.textContent = t('auth.backToSignIn', 'Back to Sign In');
    } else {
      // Switch back to login mode
      fullNameGroup.style.display = 'none';
      signInBtn.textContent = t('auth.signInAction', 'Sign In');
      signInBtn.dataset.mode = 'login';
      showRegisterBtn.textContent = t('auth.register', 'Register');
    }
  });

  // "Sign in with email" button reveals the email/password form
  document.getElementById('showEmailFormBtn').addEventListener('click', () => {
    const emailSection = document.getElementById('emailAuthSection');
    if (emailSection) {
      emailSection.style.display = emailSection.style.display === 'none' ? 'block' : 'none';
    }
  });
}

/**
 * Change language and re-render the page
 */
function changeLanguage(lang) {
  i18next.changeLanguage(lang);
  localStorage.setItem('language', lang);
  renderSignInView(); // Re-render to update all text
}

// Store pending credential for account linking
let pendingLinkingCredential = null;

/**
 * Show field error
 */
function showFieldError(fieldId, message) {
  const errorElement = document.getElementById(`${fieldId}Error`);
  if (errorElement) {
    errorElement.textContent = message;
    errorElement.style.display = 'block';
  }
}

/**
 * Hide field error
 */
function hideFieldError(fieldId) {
  const errorElement = document.getElementById(`${fieldId}Error`);
  if (errorElement) {
    errorElement.textContent = '';
    errorElement.style.display = 'none';
  }
}

/**
 * Hide all field errors
 */
function hideAllFieldErrors() {
  hideFieldError('fullName');
  hideFieldError('email');
  hideFieldError('password');
}

/**
 * Validate form fields
 */
function validateFormFields(fullName, email, password, isSignUp) {
  let isValid = true;
  hideAllFieldErrors();

  // Validate name (only for sign up)
  if (isSignUp && !fullName.trim()) {
    showFieldError('fullName', t('auth.error.nameRequired', 'Please enter your name'));
    isValid = false;
  }

  // Validate email
  if (!email.trim()) {
    showFieldError('email', t('auth.error.emailRequired', 'Please enter your email'));
    isValid = false;
  } else if (!email.includes('@') || !email.includes('.')) {
    showFieldError('email', t('auth.error.invalidEmail', 'Please enter a valid email address'));
    isValid = false;
  }

  // Validate password
  if (!password) {
    showFieldError('password', t('auth.error.passwordRequired', 'Please enter your password'));
    isValid = false;
  } else if (isSignUp && password.length < 8) {
    showFieldError(
      'password',
      t('auth.error.passwordTooShort', 'Password must be at least 8 characters')
    );
    isValid = false;
  }

  return isValid;
}

/**
 * Translate Firebase error codes to user-friendly messages
 */
function getErrorMessage(errorCode) {
  const errorMessages = {
    // OAuth errors
    'auth/popup-closed-by-user': t(
      'auth.error.popupClosed',
      'Sign-in cancelled. Please try again.'
    ),
    'auth/popup-blocked': t(
      'auth.error.popupBlocked',
      'Pop-up blocked by browser. Please allow pop-ups and try again.'
    ),
    'auth/cancelled-popup-request': t(
      'auth.error.cancelled',
      'Sign-in cancelled. Please try again.'
    ),
    'auth/account-exists-with-different-credential': t(
      'auth.error.accountExists',
      'An account already exists with the same email but different sign-in method.'
    ),
    'auth/invalid-credential': t(
      'auth.error.invalidCredential',
      'Invalid credentials. Please try again.'
    ),
    'auth/operation-not-allowed': t('auth.error.notAllowed', 'This sign-in method is not enabled.'),

    // Email/Password errors
    'auth/email-already-in-use': t('auth.emailInUse', 'An account with this email already exists.'),
    'auth/invalid-email': t('auth.error.invalidEmail', 'Please enter a valid email address.'),
    'auth/weak-password': t(
      'auth.error.weakPassword',
      'Password is too weak. Please use a stronger password.'
    ),
    'auth/user-not-found': t('auth.error.userNotFound', 'No account found with this email.'),
    'auth/wrong-password': t('auth.error.wrongPassword', 'Incorrect password. Please try again.'),
    'auth/invalid-login-credentials': t(
      'auth.error.invalidLogin',
      'Invalid email or password. Please try again.'
    ),

    // Account linking errors
    'auth/credential-already-in-use': t(
      'auth.error.credentialInUse',
      'This credential is already associated with another account.'
    ),
    'auth/provider-already-linked': t(
      'auth.error.alreadyLinked',
      'This sign-in method is already linked to your account.'
    ),
    'auth/no-current-user': t('auth.error.noUser', 'No user is currently signed in.'),
    'auth/account-exists-different-method': t(
      'auth.error.accountExistsDifferentMethod',
      'An account with this email already exists. Please sign in using the method you originally used (Google, email/password, or Facebook).'
    ),

    // Rate limiting and network errors
    'auth/too-many-requests': t(
      'auth.error.tooManyRequests',
      'Too many attempts. Please try again later.'
    ),
    'auth/network-request-failed': t(
      'auth.error.networkError',
      'Network error. Please check your connection and try again.'
    ),

    // User account errors
    'auth/user-disabled': t('auth.error.userDisabled', 'This account has been disabled.'),
    'auth/requires-recent-login': t(
      'auth.error.recentLoginRequired',
      'Please sign in again to complete this action.'
    ),
  };

  return (
    errorMessages[errorCode] || t('auth.error.unknownError', 'An unexpected error occurred. Please try again.')
  );
}

/**
 * Handle email sign-in form submission
 */
async function handleEmailSignIn(e) {
  e.preventDefault();

  const form = e.target;
  const email = form.email.value.trim();
  const password = form.password.value;
  const signInBtn = document.getElementById('signInBtn');
  const isRegisterMode = signInBtn.dataset.mode === 'register';

  // If in register mode, delegate to sign-up handler
  if (isRegisterMode) {
    await handleEmailSignUp();
    return;
  }

  // Validate fields
  if (!validateFormFields('', email, password, false)) {
    return;
  }

  try {
    signInBtn.disabled = true;
    signInBtn.textContent = t('auth.signingIn', 'Signing in...');
    hideError();
    hideSuccess();
    hideAllFieldErrors();
    showLoadingOverlay(document.querySelector('.sign-in-container') || document.body);

    const result = await signInWithEmail(email, password);

    // Check if email needs verification
    if (result.needsVerification) {
      showSuccess(
        t('auth.verifyEmailReminder', 'Please verify your email to access all features.')
      );
    }

    // Success - auth state change will handle navigation
  } catch (error) {
    hideLoadingOverlay(document.querySelector('.sign-in-container') || document.body);
    console.error('Email sign-in failed:', error);

    // Handle user suspended
    if (error.code === 'auth/user-suspended') {
      showError(
        error.message ||
          t(
            'auth.userSuspended',
            'Your account has been suspended. Please contact the administrators at admin@kassie.media'
          )
      );
      signInBtn.disabled = false;
      signInBtn.textContent = t('auth.signInAction', 'Sign In');
      return;
    }

    // Handle account linking
    if (error.needsLinking) {
      showLinkingModal(error);
      return;
    }

    // Show error under appropriate field
    if (error.code === 'auth/invalid-email') {
      showFieldError('email', getErrorMessage(error.code));
    } else if (
      error.code === 'auth/wrong-password' ||
      error.code === 'auth/invalid-login-credentials'
    ) {
      showFieldError('password', getErrorMessage(error.code));
    } else {
      showError(getErrorMessage(error.code));
    }

    signInBtn.disabled = false;
    signInBtn.textContent = t('auth.signInAction', 'Sign In');
  }
}

/**
 * Handle email sign-up button click
 */
async function handleEmailSignUp() {
  const fullName = document.getElementById('fullName').value.trim();
  const email = document.getElementById('email').value.trim();
  const password = document.getElementById('password').value;
  const signUpBtn = document.getElementById('signInBtn');

  // Validate fields
  if (!validateFormFields(fullName, email, password, true)) {
    return;
  }

  try {
    signUpBtn.disabled = true;
    signUpBtn.textContent = t('auth.signingUp', 'Creating account...');
    hideError();
    hideSuccess();
    hideAllFieldErrors();

    // Check if this is an invitation flow
    const urlParams = new URLSearchParams(window.location.search);
    const invitationToken = urlParams.get('token');
    const isInvitation = invitationToken && window.location.pathname === '/invite';

    // Bypass allowlist for family invitations
    const result = await signUpWithEmail(email, password, {
      bypassAllowlist: isInvitation,
      displayName: fullName,
    });

    // Show success message about verification email
    if (result.needsVerification) {
      showSuccess(
        t(
          'auth.verificationEmailSent',
          'Account created! Please check your email to verify your account.'
        )
      );
    }

    // Success - auth state change will handle navigation
  } catch (error) {
    console.error('Email sign-up failed:', error);

    // Handle email not allowed (allowlist) — save to waitlist
    if (error.code === 'auth/email-not-allowed') {
      try {
        const { api } = await import('../services/api.js');
        await api.post('/api/early-access/request', {
          displayName: fullName,
          email,
        });
        showSuccess(
          t(
            'auth.waitlistSaved',
            'Thank you! We have saved your details and will send you an automated invite when registration opens.'
          )
        );
      } catch (waitlistErr) {
        // 409 = duplicate (already on waitlist)
        if (waitlistErr?.status === 409) {
          showSuccess(
            t(
              'auth.waitlistAlready',
              'You are already on the waitlist. We will send you an invite when registration opens.'
            )
          );
        } else {
          showError(
            t(
              'auth.waitlistFailed',
              'Registration is not available yet. Please try again later or contact admin@kassie.media.'
            )
          );
        }
      }
      signUpBtn.disabled = false;
      signUpBtn.textContent = t('auth.createAccount', 'Create Account');
      return;
    }

    // Handle email already in use - show helpful message
    if (error.code === 'auth/email-already-in-use') {
      showFieldError(
        'email',
        t(
          'auth.emailAlreadyExists',
          'This email already has an account. Please try signing in with the "Sign In" button, or use Google/Facebook if you registered with those.'
        )
      );
      signUpBtn.disabled = false;
      signUpBtn.textContent = t('auth.createAccount', 'Create Account');
      return;
    }

    // Show error under appropriate field
    if (error.code === 'auth/invalid-email') {
      showFieldError('email', getErrorMessage(error.code));
    } else if (error.code === 'auth/weak-password') {
      showFieldError('password', getErrorMessage(error.code));
    } else {
      showError(getErrorMessage(error.code) + (error.code ? ` (${error.code})` : ''));
    }

    signUpBtn.disabled = false;
    signUpBtn.textContent = t('auth.createAccount', 'Create Account');
  }
}

/**
 * Handle forgot password button click
 */
async function handleForgotPassword() {
  const email = document.getElementById('email').value.trim();
  const forgotBtn = document.getElementById('forgotPasswordBtn');

  if (!email) {
    showError(t('auth.enterEmailForReset', 'Please enter your email address'));
    return;
  }

  try {
    hideError();
    hideSuccess();

    await sendPasswordReset(email);
    showSuccess(t('auth.passwordResetSent', 'Password reset email sent! Please check your inbox.'));

    // Change button text to confirm email was sent
    forgotBtn.textContent = t('auth.emailSent', 'Email sent');
    forgotBtn.disabled = true;

    // Re-enable after 10 seconds
    setTimeout(() => {
      forgotBtn.textContent = t('auth.forgotPassword', 'Forgot password?');
      forgotBtn.disabled = false;
    }, 10000);
  } catch (error) {
    console.error('Password reset failed:', error);
    showError(getErrorMessage(error.code));
  }
}

/**
 * Handle Google sign-in button click
 */
async function handleGoogleSignIn() {
  const btn = document.getElementById('googleSignInBtn');

  try {
    btn.disabled = true;
    hideError();
    hideSuccess();
    showLoadingOverlay(document.querySelector('.sign-in-container') || document.body);

    await signInWithGoogle();

    // Success - auth state change will handle navigation
  } catch (error) {
    hideLoadingOverlay(document.querySelector('.sign-in-container') || document.body);
    console.error('Google sign-in failed:', error);

    // Handle user suspended
    if (error.code === 'auth/user-suspended') {
      showError(
        error.message ||
          t(
            'auth.userSuspended',
            'Your account has been suspended. Please contact the administrators at admin@kassie.media'
          )
      );
      btn.disabled = false;
      return;
    }

    // Handle account linking with custom message
    if (error.code === 'auth/account-exists-different-provider' && error.message) {
      showError(error.message);
      btn.disabled = false;
      return;
    }

    // Handle account linking
    if (error.needsLinking) {
      showLinkingModal(error);
      btn.disabled = false;
      return;
    }

    showError(getErrorMessage(error.code));
    btn.disabled = false;
  }
}

/**
 * Handle Facebook sign-in button click
 */
async function handleFacebookSignIn() {
  const btn = document.getElementById('facebookSignInBtn');

  try {
    btn.disabled = true;
    hideError();
    hideSuccess();
    showLoadingOverlay(document.querySelector('.sign-in-container') || document.body);

    await signInWithFacebook();

    // Success - auth state change will handle navigation
  } catch (error) {
    hideLoadingOverlay(document.querySelector('.sign-in-container') || document.body);
    console.error('Facebook sign-in failed:', error);

    // Handle user suspended
    if (error.code === 'auth/user-suspended') {
      showError(
        error.message ||
          t(
            'auth.userSuspended',
            'Your account has been suspended. Please contact the administrators at admin@kassie.media'
          )
      );
      btn.disabled = false;
      return;
    }

    // Handle account linking with custom message
    if (error.code === 'auth/account-exists-different-provider' && error.message) {
      showError(error.message);
      btn.disabled = false;
      return;
    }

    // Handle account linking
    if (error.needsLinking) {
      showLinkingModal(error);
      btn.disabled = false;
      return;
    }

    showError(getErrorMessage(error.code));
    btn.disabled = false;
  }
}

/**
 * Show account linking modal
 */
function showLinkingModal(linkingInfo) {
  pendingLinkingCredential = linkingInfo.pendingCredential;

  const modal = document.getElementById('linkingModal');
  const message = document.getElementById('linkingMessage');

  message.textContent = linkingInfo.message;
  modal.style.display = 'flex';

  // Add confirm button handler
  document.getElementById('confirmLinkBtn').onclick = handleConfirmLinking;
}

/**
 * Hide account linking modal
 */
function hideLinkingModal() {
  const modal = document.getElementById('linkingModal');
  modal.style.display = 'none';
  pendingLinkingCredential = null;
}

/**
 * Handle account linking confirmation
 */
async function handleConfirmLinking() {
  if (!pendingLinkingCredential) {
    showError(t('auth.linkingFailed', 'Account linking failed'));
    hideLinkingModal();
    return;
  }

  try {
    await linkCredentialToCurrentUser(pendingLinkingCredential);
    showSuccess(t('auth.accountsLinked', 'Accounts linked successfully!'));
    hideLinkingModal();

    // Success - auth state change will handle navigation
  } catch (error) {
    console.error('Account linking failed:', error);
    showError(getErrorMessage(error.code));
    hideLinkingModal();
  }
}

/**
 * Show error message
 */
function showError(message) {
  const errorDiv = document.getElementById('errorMessage');
  errorDiv.textContent = message;
  errorDiv.style.display = 'block';
}

/**
 * Hide error message
 */
function hideError() {
  const errorDiv = document.getElementById('errorMessage');
  errorDiv.style.display = 'none';
}

/**
 * Show success message
 */
function showSuccess(message) {
  const successDiv = document.getElementById('successMessage');
  successDiv.textContent = message;
  successDiv.style.display = 'block';
}

/**
 * Hide success message
 */
function hideSuccess() {
  const successDiv = document.getElementById('successMessage');
  successDiv.style.display = 'none';
}
